import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search, ChevronRight, ChevronDown, X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { DOCS_SECTIONS, DOCS_TOPIC_MAP } from './index';

// ── Simple markdown renderer ─────────────────────────────────────────────────
function renderContent(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={i} className="text-base font-bold mt-5 mb-2 first:mt-0" style={{ color: 'var(--doc-heading)' }}>
          {line.slice(3)}
        </h2>
      );
    } else if (line.startsWith('> ')) {
      elements.push(
        <div key={i} className="my-3 pl-3 py-2 pr-3 rounded-lg border-l-4 text-xs leading-relaxed"
          style={{ borderColor: 'var(--doc-accent)', backgroundColor: 'var(--doc-tip-bg)', color: 'var(--doc-tip-text)' }}>
          {inlineFormat(line.slice(2))}
        </div>
      );
    } else if (line.startsWith('---')) {
      elements.push(<hr key={i} className="my-4 opacity-20" style={{ borderColor: 'var(--doc-border)' }} />);
    } else if (line.match(/^\| .+ \|/)) {
      const tableLines = [];
      let j = i;
      while (j < lines.length && lines[j].match(/^\|/)) {
        tableLines.push(lines[j]);
        j++;
      }
      const headers = tableLines[0].split('|').filter(Boolean).map(s => s.trim());
      const rows = tableLines.slice(2).map(r => r.split('|').filter(Boolean).map(s => s.trim()));
      elements.push(
        <div key={i} className="my-3 overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr>
                {headers.map((h, hi) => (
                  <th key={hi} className="text-left px-3 py-1.5 font-semibold"
                    style={{ backgroundColor: 'var(--doc-table-head)', color: 'var(--doc-heading)', borderBottom: '1px solid var(--doc-border)' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} style={{ backgroundColor: ri % 2 === 1 ? 'var(--doc-table-alt)' : 'transparent' }}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-3 py-1.5" style={{ color: 'var(--doc-body)', borderBottom: '1px solid var(--doc-border)' }}>
                      {inlineFormat(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      i = j;
      continue;
    } else if (line.startsWith('- ')) {
      const items = [];
      let j = i;
      while (j < lines.length && lines[j].startsWith('- ')) {
        items.push(lines[j].slice(2));
        j++;
      }
      elements.push(
        <ul key={i} className="my-2 space-y-1 pl-4">
          {items.map((item, ii) => (
            <li key={ii} className="text-xs leading-relaxed flex gap-2" style={{ color: 'var(--doc-body)' }}>
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--doc-accent)' }} />
              {inlineFormat(item)}
            </li>
          ))}
        </ul>
      );
      i = j;
      continue;
    } else if (line.startsWith('- [ ] ') || line.startsWith('- [x] ')) {
      const items = [];
      let j = i;
      while (j < lines.length && (lines[j].startsWith('- [ ]') || lines[j].startsWith('- [x]'))) {
        const checked = lines[j].startsWith('- [x]');
        items.push({ text: lines[j].slice(6), checked });
        j++;
      }
      elements.push(
        <ul key={i} className="my-2 space-y-1 pl-2">
          {items.map((item, ii) => (
            <li key={ii} className="text-xs leading-relaxed flex gap-2 items-start" style={{ color: 'var(--doc-body)' }}>
              <span className="mt-0.5 flex-shrink-0 text-sm">{item.checked ? '✅' : '☐'}</span>
              {inlineFormat(item.text)}
            </li>
          ))}
        </ul>
      );
      i = j;
      continue;
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-1.5" />);
    } else {
      elements.push(
        <p key={i} className="text-xs leading-relaxed" style={{ color: 'var(--doc-body)' }}>
          {inlineFormat(line)}
        </p>
      );
    }
    i++;
  }
  return elements;
}

function inlineFormat(text) {
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const raw = match[0];
    if (raw.startsWith('**')) {
      parts.push(<strong key={match.index} style={{ color: 'var(--doc-heading)' }}>{raw.slice(2, -2)}</strong>);
    } else {
      parts.push(
        <code key={match.index} className="px-1 py-0.5 rounded text-[14px] font-mono"
          style={{ backgroundColor: 'var(--doc-code-bg)', color: 'var(--doc-code-text)' }}>
          {raw.slice(1, -1)}
        </code>
      );
    }
    last = match.index + raw.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
}

// ── Main DocsPage ────────────────────────────────────────────────────────────
export default function DocsPage({ onBack }) {
  const { colors } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [openSections, setOpenSections] = useState(() =>
    DOCS_SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: true }), {})
  );

  const sectionId  = searchParams.get('section') || DOCS_SECTIONS[0]?.id;
  const topicId    = searchParams.get('topic')   || DOCS_SECTIONS[0]?.topics[0]?.id;
  const activeTopic = DOCS_TOPIC_MAP[topicId] || null;

  const setTopic = (sid, tid) => {
    setSearchParams({ section: sid, topic: tid }, { replace: true });
    setSearchQuery('');
  };

  const toggleSection = (sid) => {
    setOpenSections(prev => ({ ...prev, [sid]: !prev[sid] }));
  };

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    const results = [];
    DOCS_SECTIONS.forEach(section => {
      section.topics.forEach(topic => {
        if (
          topic.title.toLowerCase().includes(q) ||
          (topic.content || '').toLowerCase().includes(q)
        ) {
          results.push({ ...topic, sectionId: section.id, sectionTitle: section.title });
        }
      });
    });
    return results;
  }, [searchQuery]);

  const docVars = {
    '--doc-heading':    colors.primaryText,
    '--doc-body':       colors.secondaryText,
    '--doc-accent':     colors.accent,
    '--doc-border':     colors.border,
    '--doc-tip-bg':     `${colors.accent}15`,
    '--doc-tip-text':   colors.primaryText,
    '--doc-table-head': `${colors.accent}10`,
    '--doc-table-alt':  `${colors.border}20`,
    '--doc-code-bg':    `${colors.border}40`,
    '--doc-code-text':  colors.accent,
  };

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: colors.background, ...docVars }}>
      {/* Header */}
      <div className="flex-shrink-0 flex items-center gap-3 px-3 py-2.5 border-b"
        style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderColor: colors.border }}>
        <button onClick={onBack} className="p-1.5 rounded-lg"
          style={{ backgroundColor: colors.background, color: colors.primaryText }}>
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h1 className="text-sm font-bold" style={{ color: colors.primaryText }}>Documentation</h1>
          <p className="text-[13px]" style={{ color: colors.secondaryText }}>Everything about TradeReflex AI</p>
        </div>
      </div>

      {/* Search bar */}
      <div className="flex-shrink-0 px-3 py-2 border-b" style={{ borderColor: colors.border }}>
        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
          <Search className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.secondaryText }} />
          <input
            type="text"
            placeholder="Search documentation..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent text-xs outline-none"
            style={{ color: colors.primaryText }}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')}>
              <X className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />
            </button>
          )}
        </div>
      </div>

      {/* Search results */}
      {searchQuery && (
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {searchResults.length === 0 ? (
            <p className="text-xs text-center py-8" style={{ color: colors.secondaryText }}>
              No results for "{searchQuery}"
            </p>
          ) : (
            <div className="space-y-2">
              <p className="text-[13px] font-semibold uppercase tracking-wide mb-3" style={{ color: colors.secondaryText }}>
                {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
              </p>
              {searchResults.map(result => (
                <button key={result.id} onClick={() => setTopic(result.sectionId, result.id)}
                  className="w-full text-left p-3 rounded-lg border"
                  style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderColor: colors.border }}>
                  <div className="text-[13px] mb-0.5" style={{ color: colors.accent }}>{result.sectionTitle}</div>
                  <div className="text-xs font-semibold" style={{ color: colors.primaryText }}>{result.title}</div>
                  <div className="text-[13px] mt-0.5 line-clamp-2" style={{ color: colors.secondaryText }}>
                    {(result.content || '').replace(/[#>*`-]/g, '').slice(0, 100)}...
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Two-column layout */}
      {!searchQuery && (
        <div className="flex flex-1 min-h-0">
          {/* Sidebar */}
          <div className="flex-shrink-0 w-48 border-r overflow-y-auto py-2"
            style={{ borderColor: colors.border, background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
            {DOCS_SECTIONS.map(section => (
              <div key={section.id} className="mb-1">
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left"
                >
                  <span className="text-sm">{section.icon}</span>
                  <span className="flex-1 text-[14px] font-semibold" style={{ color: colors.primaryText }}>
                    {section.title}
                  </span>
                  {openSections[section.id]
                    ? <ChevronDown className="w-3 h-3 flex-shrink-0" style={{ color: colors.secondaryText }} />
                    : <ChevronRight className="w-3 h-3 flex-shrink-0" style={{ color: colors.secondaryText }} />
                  }
                </button>

                {openSections[section.id] && (
                  <div className="ml-4 pl-3 border-l" style={{ borderColor: colors.border }}>
                    {section.topics.map(topic => {
                      const isActive = topic.id === topicId;
                      return (
                        <button
                          key={topic.id}
                          onClick={() => setTopic(section.id, topic.id)}
                          className="w-full text-left px-2 py-1.5 rounded text-[14px] transition-all"
                          style={{
                            color:           isActive ? colors.accent : colors.secondaryText,
                            backgroundColor: isActive ? `${colors.accent}15` : 'transparent',
                            fontWeight:      isActive ? 600 : 400,
                          }}
                        >
                          {topic.title}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Content area */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {activeTopic ? (
              <>
                <div className="mb-1">
                  <span className="text-[13px] font-semibold uppercase tracking-wide" style={{ color: colors.accent }}>
                    {activeTopic.sectionTitle}
                  </span>
                </div>
                <div className="pb-8">
                  {renderContent(activeTopic.content)}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-xs" style={{ color: colors.secondaryText }}>Select a topic from the sidebar</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
